import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import styles from './AdminUserCreate.module.css';
import { userApi } from '../../services/userApi';

interface Firm {
  Firm_ID: string;
  abbrev: string;
  name?: string;
  status?: number;
}

interface City {
  City_ID: string;
  name: string;
  Country?: {
    name: string;
    abbrev?: string;
  };
  country?: {
    name: string;
    abbrev?: string;
  };
}

interface NewUser {
  firstname: string;
  lastname: string;
  title?: string;
  mail: string;
  jobtitle?: string;
  gender?: string;
  linkedin?: string;
  profile?: string;
  Firm_ID?: string;
  City_ID?: string;
  verification?: number;
  picture?: string;
  department?: string;
  employee_group?: string;
}

const Admin_User_Create: React.FC = () => {
  const [formData, setFormData] = useState<NewUser>({
    firstname: '',
    lastname: '',
    title: '',
    mail: '',
    jobtitle: '',
    gender: 'Male',
    department: '',
    employee_group: ''
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [firms, setFirms] = useState<Firm[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Search states for firm and city
  const [firmSearch, setFirmSearch] = useState<string>('');
  const [citySearch, setCitySearch] = useState<string>('');
  const [showFirmDropdown, setShowFirmDropdown] = useState<boolean>(false);
  const [showCityDropdown, setShowCityDropdown] = useState<boolean>(false);
  
  // Create a ref to track if component is mounted
  const isMountedRef = useRef(true);

  // Helper function to extract error message from API response
  const getErrorMessage = async (error: any): Promise<string> => {
    if (error?.response?.json) {
      try {
        const errorData = await error.response.json();
        return errorData.detail || errorData.message || 'An error occurred';
      } catch {
        return error.message || 'An error occurred';
      }
    } else if (error?.detail) {
      return error.detail;
    } else if (error?.message) {
      return error.message;
    } else if (typeof error === 'string') {
      return error;
    }
    return 'An unexpected error occurred';
  };

  // Handle navigation with controlled unmounting
  const navigateWithDelay = (path: string) => {
    // Short delay to allow cleanup of ResizeObservers
    setTimeout(() => {
      if (isMountedRef.current) {
        navigate(path);
      }
    }, 50);
  };

  useEffect(() => {
    // Fetch firms and cities on component mount
    fetchFirms();
    fetchCities();
    
    // Close dropdowns when clicking outside
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('#inputfirm') && !target.closest('[style*="zIndex: 1000"]')) {
        setShowFirmDropdown(false);
      }
      if (!target.closest('#inputcity') && !target.closest('[style*="zIndex: 1000"]')) {
        setShowCityDropdown(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    
    // Cleanup function to handle unmounting gracefully
    return () => {
      isMountedRef.current = false;
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  const fetchFirms = async () => {
    try {
      const firmsData = await userApi.getFirmsLookup();
      console.log(`Loaded ${firmsData.length} firms:`, firmsData);
      setFirms(firmsData);
    } catch (error) {
      console.error('Error fetching firms:', error);
      const errorMessage = await getErrorMessage(error);
      toast.error(`Failed to load firms data: ${errorMessage}`, {
        position: "top-right",
        autoClose: 5000
      });
    }
  };

  const fetchCities = async () => {
    try {
      const citiesData = await userApi.getCitiesLookup();
      console.log(`Loaded ${citiesData.length} cities:`, citiesData);
      setCities(citiesData);
    } catch (error) {
      console.error('Error fetching cities:', error);
      const errorMessage = await getErrorMessage(error);
      toast.error(`Failed to load cities data: ${errorMessage}`, {
        position: "top-right",
        autoClose: 5000
      });
    }
  };

  // Filter firms based on search
  const filteredFirms = firms.filter(firm => 
    (firm.name?.toLowerCase() || '').includes(firmSearch.toLowerCase()) ||
    firm.abbrev.toLowerCase().includes(firmSearch.toLowerCase())
  );

  // Filter cities based on search
  const filteredCities = cities.filter(city => {
    const countryData = city.country || city.Country;
    return city.name.toLowerCase().includes(citySearch.toLowerCase()) ||
      (countryData?.name?.toLowerCase() || '').includes(citySearch.toLowerCase());
  });

  // Get selected firm name for display
  const getSelectedFirmName = () => {
    if (!formData.Firm_ID) return '';
    const firm = firms.find(f => f.Firm_ID === formData.Firm_ID);
    if (!firm) return '';
    const displayName = firm.name || firm.abbrev;
    return firm.status !== 1 ? `${displayName} (inactive)` : displayName;
  };

  // Get selected city name for display
  const getSelectedCityName = () => {
    if (!formData.City_ID) return '';
    const city = cities.find(c => c.City_ID === formData.City_ID);
    if (!city) return '';
    const countryData = city.country || city.Country;
    const countryName = countryData?.name || '';
    const countryAbbrev = countryData?.abbrev || '';
    return `${city.name}${countryName ? ` (${countryName})` : ''}${countryAbbrev ? ` [${countryAbbrev}]` : ''}`;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { id, value, type } = e.target as HTMLInputElement;
    
    if (type === 'checkbox') {
      const isChecked = (e.target as HTMLInputElement).checked;
      if (id === 'inputVerification') {
        setFormData(prev => ({
          ...prev,
          verification: isChecked ? 1 : 0
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          [id.replace('input', '').toLowerCase()]: isChecked
        }));
      }
    } else if (id === 'inputfirm') {
      setFormData(prev => ({
        ...prev,
        Firm_ID: value
      }));
    } else if (id === 'inputcity') {
      setFormData(prev => ({
        ...prev,
        City_ID: value
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [id.replace('input', '').toLowerCase()]: value
      }));
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        // Extract base64 data
        const fullBase64 = reader.result?.toString();
        const base64String = fullBase64?.split(',')[1];
        if (base64String) {
          setFormData(prev => ({
            ...prev,
            picture: base64String
          }));
          // Set image preview with full base64 string for immediate display
          setImagePreview(fullBase64 || null);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemovePicture = () => {
    setImagePreview(null);
    setFormData(prev => ({
      ...prev,
      picture: ''
    }));
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Basic validation - only firstname, lastname, mail are required (matching backend UserUpdate model)
    if (!formData.firstname || !formData.lastname || !formData.mail) {
      toast.error('Please fill out all required fields (First name, Last name, Email)', {
        position: "top-right",
        autoClose: 5000
      });
      return;
    }
    
    try {
      setLoading(true);
      const response = await userApi.createUser(formData);
      
      // Backend returns { success: true, message: "...", user: { User_ID: "...", ... } }
      const userId = response.user?.User_ID;
      
      if (!userId) {
        throw new Error('User created but no User_ID returned');
      }
      
      // Show success toast and navigate with delay
      toast.success('User created successfully', {
        position: "top-right",
        autoClose: 3000,
        onClose: () => {
          // Only navigate after the toast is shown and if still mounted
          if (isMountedRef.current) {
            navigateWithDelay(`/admin/users/${userId}`);
          }
        }
      });
      
    } catch (error) {
      console.error('Error creating user:', error);
      const errorMessage = await getErrorMessage(error);
      toast.error(`Failed to create user: ${errorMessage}`, {
        position: "top-right",
        autoClose: 5000
      });
      setLoading(false);
    }
  };

  return (
    <div className={styles.root}>
      <div className="container mt-4">
        <nav aria-label="breadcrumb">
          <ol className={`breadcrumb`}>
            <li className={`breadcrumb-item `}>
              <Link to="/admin"><i className="bi bi-house-door me-1"></i>Admin</Link>
            </li>
            <li className={`breadcrumb-item `}>
              <Link to="/admin/users">User Management</Link>
            </li>
            <li className={`breadcrumb-item active `}>Create New User</li>
          </ol>
        </nav>
        
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h3 className="mb-0">Create New User</h3>
        </div>

        <div className="container-xl px-4 mt-4">
          <form id="profileForm" onSubmit={handleSubmit}>
            <div className="row">
              <div className="col-xl-4">
                {/* Profile picture card */}
                <div className={`card mb-4 mb-xl-0 ${styles.userFormCard}`}>
                  <div className={`card-header ${styles.cardHeader}`}>Profile Picture</div>
                  <div className={`card-body text-center ${styles.cardBody}`}>
                    {/* Profile picture image */}
                    <img 
                      className={`img-account-profile rounded-circle mb-2 ${styles.profilePicture}`}
                      src={imagePreview || "/static/images/user-profile-icon.png"} 
                      alt="Default Profile" 
                    />
                    <div className={`small font-italic text-muted mb-4 ${styles.profileUploadInfo}`}>JPG or PNG no larger than 5 MB</div>
                    
                    {/* Hidden file input */}
                    <input 
                      type="file" 
                      id="fileInput"
                      ref={fileInputRef}
                      accept="image/png, image/jpeg" 
                      className="d-none" 
                      onChange={handleFileUpload}
                    />
                    
                    {/* Profile picture upload button */}
                    <button 
                      className="btn btn-primary w-100" 
                      type="button"
                      onClick={() => document.getElementById('fileInput')?.click()}
                    >
                      <i className="bi bi-upload me-2"></i>
                      Upload new image
                    </button>

                    {/* Remove picture button */}
                    {imagePreview && (
                      <button 
                        type="button"
                        className="btn btn-outline-danger w-100 mt-2" 
                        onClick={handleRemovePicture}
                      >
                        <i className="bi bi-trash me-2"></i>
                        Remove Picture
                      </button>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="col-xl-8">
                {/* Account details card */}
                <div className={`card mb-4 ${styles.userFormCard}`}>
                  <div className={`card-header ${styles.cardHeader}`}>Account Details</div>
                  <div className={`card-body ${styles.cardBody}`}>
                    {/* First name and Last name */}
                    <div className={`row gx-3 mb-3 ${styles.userFormRow}`}>
                      {/* Form Group (first name) */}
                      <div className={`col-md-6 ${styles.formGroup}`}>
                        <label className={`small mb-1 ${styles.formLabel}`} htmlFor="inputFirstname">First name</label>
                        <input 
                          className={`form-control ${styles.formControl}`} 
                          id="inputFirstname" 
                          type="text"
                          placeholder="Enter first name" 
                          value={formData.firstname || ''} 
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                      {/* Form Group (last name) */}
                      <div className={`col-md-6 ${styles.formGroup}`}>
                        <label className={`small mb-1 ${styles.formLabel}`} htmlFor="inputLastname">Last name</label>
                        <input 
                          className={`form-control ${styles.formControl}`} 
                          id="inputLastname" 
                          type="text"
                          placeholder="Enter last name" 
                          value={formData.lastname || ''} 
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                    </div>
                    
                    {/* Title and Verification */}
                    <div className={`row gx-3 mb-3 ${styles.userFormRow}`}>
                      {/* Form Group (title) */}
                      <div className={`col-md-6 ${styles.formGroup}`}>
                        <label className={`small mb-1 ${styles.formLabel}`} htmlFor="inputTitle">Title</label>
                        <input 
                          className={`form-control ${styles.formControl}`} 
                          id="inputTitle" 
                          type="text"
                          placeholder="Enter title (e.g. Dr., Prof.)" 
                          value={formData.title || ''} 
                          onChange={handleInputChange}
                        />
                      </div>

                      <div className={`col-md-6 ${styles.formGroup}`}>
                        <div className="form-check mt-4">
                          <input 
                            className={`form-check-input ${styles.formCheckInput}`} 
                            type="checkbox" 
                            id="inputVerification"
                            checked={formData.verification === 1} 
                            onChange={handleInputChange}
                          />
                          <label className={`form-check-label ${styles.formCheckLabel}`} htmlFor="inputVerification">
                            Verification
                          </label>
                        </div>
                      </div>
                    </div>

                    {/* Email address */}
                    <div className={`row gx-3 mb-3 ${styles.userFormRow}`}>
                      {/* Form Group (email address) */}
                      <div className={`col-md-12 ${styles.formGroup}`}>
                        <label className={`small mb-1 ${styles.formLabel}`} htmlFor="inputMail">Email address</label>
                        <input 
                          className={`form-control ${styles.formControl}`} 
                          id="inputMail" 
                          type="email"
                          placeholder="Enter email address" 
                          value={formData.mail || ''} 
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                    </div>

                    {/* Firm */}
                    <div className={`row gx-3 mb-3 ${styles.userFormRow}`}>
                      {/* Form Group (firm) */}
                      <div className={`col-md-12 ${styles.formGroup}`} style={{ position: 'relative' }}>
                        <label className={`small mb-1 ${styles.formLabel}`} htmlFor="inputfirm">Firm</label>
                        <input 
                          className={`form-control ${styles.formControl}`} 
                          id="inputfirm"
                          type="text"
                          placeholder="Search firm..."
                          value={formData.Firm_ID ? getSelectedFirmName() : firmSearch}
                          onChange={(e) => {
                            setFirmSearch(e.target.value);
                            setFormData(prev => ({ ...prev, Firm_ID: '' }));
                            setShowFirmDropdown(true);
                          }}
                          onFocus={() => setShowFirmDropdown(true)}
                          autoComplete="off"
                        />
                        {showFirmDropdown && filteredFirms.length > 0 && (
                          <div style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            maxHeight: '200px',
                            overflowY: 'auto',
                            backgroundColor: 'white',
                            border: '1px solid #dee2e6',
                            borderRadius: '0.375rem',
                            boxShadow: '0 0.5rem 1rem rgba(0, 0, 0, 0.15)',
                            zIndex: 1000,
                            marginTop: '0.25rem'
                          }}>
                            {filteredFirms.map(firm => (
                              <div
                                key={firm.Firm_ID}
                                style={{
                                  padding: '0.5rem 1rem',
                                  cursor: 'pointer',
                                  borderBottom: '1px solid #f0f0f0'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                                onClick={() => {
                                  setFormData(prev => ({ ...prev, Firm_ID: firm.Firm_ID }));
                                  setFirmSearch('');
                                  setShowFirmDropdown(false);
                                }}
                              >
                                {firm.name || firm.abbrev}{firm.status !== 1 ? ' (inactive)' : ''}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* LinkedIn and Profile */}
                    <div className={`row gx-3 mb-3 ${styles.userFormRow}`}>
                      <div className={`col-md-6 ${styles.formGroup}`}>
                        <label className={`small mb-1 ${styles.formLabel}`} htmlFor="inputLinkedin">LinkedIn</label>
                        <input 
                          className={`form-control ${styles.formControl}`} 
                          id="inputLinkedin" 
                          type="url"
                          placeholder="Enter LinkedIn URL" 
                          value={formData.linkedin || ''} 
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className={`col-md-6 ${styles.formGroup}`}>
                        <label className={`small mb-1 ${styles.formLabel}`} htmlFor="inputProfile">Profile</label>
                        <input 
                          className={`form-control ${styles.formControl}`} 
                          id="inputProfile" 
                          type="text"
                          placeholder="Enter profile" 
                          value={formData.profile || ''} 
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>

                    {/* Job Title and Gender */}
                    <div className={`row gx-3 mb-3 ${styles.userFormRow}`}>
                      <div className={`col-md-6 ${styles.formGroup}`}>
                        <label className={`small mb-1 ${styles.formLabel}`} htmlFor="inputJobtitle">Jobtitle</label>
                        <input 
                          className={`form-control ${styles.formControl}`} 
                          id="inputJobtitle" 
                          type="text"
                          placeholder="Enter job title" 
                          value={formData.jobtitle || ''} 
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className={`col-md-6 ${styles.formGroup}`}>
                        <label className={`small mb-1 ${styles.formLabel}`} htmlFor="inputGender">Gender</label>
                        <select 
                          className={`form-control ${styles.formControl}`} 
                          id="inputGender"
                          value={formData.gender || 'Male'} 
                          onChange={handleInputChange}
                        >
                          <option value="">Select gender</option>
                          <option value="Male">Male</option>
                          <option value="Female">Female</option>
                          <option value="Diverse">Diverse</option>
                        </select>
                      </div>
                    </div>

                    {/* City */}
                    <div className={`row gx-3 mb-3 ${styles.userFormRow}`}>
                      <div className={`col-md-12 ${styles.formGroup}`} style={{ position: 'relative' }}>
                        <label className={`small mb-1 ${styles.formLabel}`} htmlFor="inputcity">City</label>
                        <input 
                          className={`form-control ${styles.formControl}`} 
                          id="inputcity"
                          type="text"
                          placeholder="Search city..."
                          value={formData.City_ID ? getSelectedCityName() : citySearch}
                          onChange={(e) => {
                            setCitySearch(e.target.value);
                            setFormData(prev => ({ ...prev, City_ID: '' }));
                            setShowCityDropdown(true);
                          }}
                          onFocus={() => setShowCityDropdown(true)}
                          autoComplete="off"
                        />
                        {showCityDropdown && filteredCities.length > 0 && (
                          <div style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            maxHeight: '200px',
                            overflowY: 'auto',
                            backgroundColor: 'white',
                            border: '1px solid #dee2e6',
                            borderRadius: '0.375rem',
                            boxShadow: '0 0.5rem 1rem rgba(0, 0, 0, 0.15)',
                            zIndex: 1000,
                            marginTop: '0.25rem'
                          }}>
                            {filteredCities.map(city => {
                              const countryData = city.country || city.Country;
                              return (
                                <div
                                  key={city.City_ID}
                                  style={{
                                    padding: '0.5rem 1rem',
                                    cursor: 'pointer',
                                    borderBottom: '1px solid #f0f0f0'
                                  }}
                                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                                  onClick={() => {
                                    setFormData(prev => ({ ...prev, City_ID: city.City_ID }));
                                    setCitySearch('');
                                    setShowCityDropdown(false);
                                  }}
                                >
                                  {city.name}{countryData ? ` (${countryData.name})` : ''}{countryData?.abbrev ? ` [${countryData.abbrev}]` : ''}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>

                    <hr />

                    <div className={`row gx-3 mb-3 ${styles.userFormRow}`}>
                      {/* Form Group (department) */}
                      <div className={`col-md-6 ${styles.formGroup}`}>
                        <label className={`small mb-1 ${styles.formLabel}`} htmlFor="inputDepartment">Department</label>
                        <input 
                          className={`form-control ${styles.formControl}`} 
                          id="inputDepartment" 
                          type="text"
                          placeholder="Enter department" 
                          value={formData.department || ''} 
                          onChange={handleInputChange}
                        />
                      </div>
                      {/* Form Group (employee group) */}
                      <div className={`col-md-6 ${styles.formGroup}`}>
                        <label className={`small mb-1 ${styles.formLabel}`} htmlFor="inputEmployee_group">Employee Group</label>
                        <input 
                          className={`form-control ${styles.formControl}`} 
                          id="inputEmployee_group" 
                          type="text"
                          placeholder="Enter employee group" 
                          value={formData.employee_group || ''} 
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>

                    <hr className="my-4" />

                    {/* Submit button */}
                    <div className={styles.formButtons}>
                      <Link to="/admin/users" className={`btn btn-outline-secondary ${styles.btnFormCancel}`}>
                        <i className="bi bi-x-circle"></i>
                        <span>Cancel</span>
                      </Link>
                      <button 
                        className={`btn btn-success ${styles.btnFormSubmit}`} 
                        type="submit" 
                        disabled={loading}
                      >
                        {loading ? (
                          <>
                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                            <span>Creating...</span>
                          </>
                        ) : (
                          <>
                            <i className="bi bi-person-plus-fill"></i>
                            <span>Create User</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
          </div>
  );
};

export default Admin_User_Create;
